import { ActionIcon, Badge, Box, Flex, Group, Image, Modal, Text } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { Carousel } from "../Carousel/Carousel";
import classNames from "./ImageGalleryRevamped.module.css";

import { LoadedImage } from "@/components/elements";
import { IconArrowLeft, IconArrowRight, IconFlag, IconTrash } from "@/components/icons";
import { FileUploader } from "@/components/widgets";
import { UserFeedback } from "@/components/widgets/UserFeedback";
import { env } from "@/config/env";

import type { Attachment } from "@/types/attachment";
import type { MantineStyleProps } from "@mantine/core";
import type { EmblaCarouselType } from "embla-carousel";
import type { FC } from "react";

export type ImageGalleryRevampedProps = {
  images?: Attachment[];
  height?: number | string;
  maxPreviews?: number;
  previewPosition?: "bottom" | "side";
  modalTitle?: string;
  onUpload?: (files: any[]) => Promise<any>;
  onPlaceholderClick?: () => void;
  uploading?: boolean;
  isPlaceholderVisible?: boolean;
  uploadingButton?: React.ReactNode;
  onEditorButtonClick?: () => void;
  onRemoveImage?: (image: Attachment) => Promise<boolean>;
  removableImageIds?: string[];
  hideStockImages?: boolean;
} & MantineStyleProps;

export const ImageGalleryRevamped: FC<ImageGalleryRevampedProps> = ({
  images: sourceImages = [],
  height,
  maxPreviews = 5,
  previewPosition = "bottom",
  onUpload,
  onPlaceholderClick,
  uploading,
  isPlaceholderVisible = false,
  uploadingButton,
  onEditorButtonClick,
  onRemoveImage,
  removableImageIds = [],
  hideStockImages = false,
  ...props
}) => {
  const { t } = useTranslation();
  const images = hideStockImages ? sourceImages.filter((image) => Boolean(image.id)) : sourceImages;

  const [activeIndex, setActiveIndex] = useState(0);
  const [opened, { open, close }] = useDisclosure(false);
  const [feedbackOpened, { toggle: toggleFeedback }] = useDisclosure(false);
  const [feedbackImage, setFeedbackImage] = useState<Attachment | null>(null);
  const [thumbnailEmbla, setThumbnailEmbla] = useState<EmblaCarouselType | null>(null);
  const [removingImage, setRemovingImage] = useState(false);

  const onSlideChange = (index: number) => {
    if (images.length === 0) return;
    setActiveIndex((index + images.length) % images.length);
  };

  const handleRemoveImage = async () => {
    const image = images[activeIndex];
    if (!image?.id || !onRemoveImage || !removableImageIds.includes(image.id)) return;

    setRemovingImage(true);
    try {
      const removed = await onRemoveImage(image);
      if (removed) {
        if (images.length === 1) {
          close();
        } else {
          setActiveIndex((index) => Math.min(index, images.length - 2));
        }
      }
    } finally {
      setRemovingImage(false);
    }
  };

  useEffect(() => {
    if (!opened || images.length < 2) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "ArrowRight") {
        event.preventDefault();
        onSlideChange(activeIndex + 1);
      }

      if (event.key === "ArrowLeft") {
        event.preventDefault();
        onSlideChange(activeIndex - 1);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [activeIndex, images.length, opened]);

  useEffect(() => {
    if (!thumbnailEmbla) return;

    thumbnailEmbla.scrollTo(activeIndex);
  }, [activeIndex, thumbnailEmbla]);

  if (isPlaceholderVisible && (!images || images.length === 0)) {
    if (onPlaceholderClick) {
      return (
        <Box w="100%" style={{ cursor: "pointer" }} onClick={onPlaceholderClick} {...props}>
          <Box style={{ pointerEvents: "none" }}>
            <FileUploader
              accept={["image/*"]}
              maxSize={10 * 1024 * 1024}
              multiple
              labels={{
                dropzoneTitle: t("car.click_to_edit_gallery"),
                dropzoneDescription: t("documents.upload_description"),
              }}
              loading={uploading}
              hideSaveButton
              onSave={async () => {}}
            />
          </Box>
        </Box>
      );
    }

    return (
      <Box w="100%" {...props}>
        <FileUploader
          accept={["image/*"]}
          maxSize={10 * 1024 * 1024}
          multiple
          labels={{ dropzoneDescription: t("documents.upload_description") }}
          loading={uploading}
          onCancel={() => {}}
          onSave={async (files) => {
            if (!onUpload) return;
            await onUpload(files);
          }}
        />
      </Box>
    );
  }

  const showPreviews = Math.min(maxPreviews, images.length);
  const hasMoreImages = images.length > maxPreviews;
  const moreImagesCount = images.length - maxPreviews;
  const isSideLayout = previewPosition === "side" && images.length > 1;
  const sidePreviewImages = images.slice(1, 4);
  const hasMoreSideImages = images.length >= 5;

  return (
    <>
      <Box w="100%" {...props}>
        <Box className={isSideLayout ? classNames.sideLayout : undefined}>
          <Carousel
            className={isSideLayout ? classNames.sideCarousel : undefined}
            draggable={false}
            withControls={false}
            withIndicators={images.length > 1}
            height={isSideLayout ? "100%" : height}
            initialSlide={activeIndex}
            onSlideChange={onSlideChange}
            slideSize="100%"
            emblaOptions={{
              align: "center",
              slidesToScroll: 1,
              containScroll: false,
            }}
            pb={0}
            slides={images.map((image, index) => (
              <Carousel.Slide key={index}>
                <Box style={{ position: "relative" }}>
                  {image.path.includes(env.API_URL) ? (
                    <LoadedImage
                      image={image}
                      className={classNames.mainImage}
                      onClick={open}
                      onDragStart={(event) => event.preventDefault()}
                      style={{ cursor: "pointer" }}
                    />
                  ) : (
                    <>
                      <Image
                        src={image.path}
                        alt={image.name}
                        className={classNames.mainImage}
                        radius="md"
                        fit="cover"
                        draggable={false}
                        onClick={open}
                        style={{ cursor: "pointer" }}
                      />
                      {!image.id && (
                        <Badge
                          pos="absolute"
                          bottom={8}
                          right={8}
                          variant="white"
                          c="dark"
                          style={{ opacity: "70%", cursor: "pointer" }}
                          onClick={(event) => {
                            event.stopPropagation();
                            setFeedbackImage({
                              ...image,
                              id: `gallery-image-${index}`,
                            });
                            toggleFeedback();
                          }}
                        >
                          <Flex align="center" gap="sm">
                            <IconFlag size={14} />
                            {t("car.carburateResource")}
                          </Flex>
                        </Badge>
                      )}
                    </>
                  )}
                </Box>
              </Carousel.Slide>
            ))}
            slideProps={{ m: 0 }}
          />

          {isSideLayout && (
            <Box className={classNames.sidePreviewContainer}>
              {sidePreviewImages.map((image, index) => (
                <Box className={classNames.sidePreviewSlot} key={image.id ?? index}>
                  {image.path.includes(env.API_URL) ? (
                    <LoadedImage
                      hideLoader
                      image={image}
                      className={classNames.sidePreviewImage}
                      onClick={() => onSlideChange(index + 1)}
                      radius="md"
                    />
                  ) : (
                    <Image
                      src={image.path}
                      alt={image.name}
                      className={classNames.sidePreviewImage}
                      onClick={() => onSlideChange(index + 1)}
                      radius="md"
                    />
                  )}
                </Box>
              ))}
              {hasMoreSideImages && (
                <Box className={classNames.moreImages} onClick={open}>
                  {images[4].path.includes(env.API_URL) ? (
                    <LoadedImage
                      hideLoader
                      image={images[4]}
                      className={classNames.moreImageBackground}
                    />
                  ) : (
                    <Image
                      src={images[4].path}
                      alt={images[4].name}
                      className={classNames.moreImageBackground}
                    />
                  )}
                  <Text c="white">
                    {t("car.allPhotos", { count: images.length < 99 ? images.length : "99+" })}
                  </Text>
                </Box>
              )}
            </Box>
          )}
        </Box>
        {!isSideLayout && images.length > 1 && (
          <Box className={classNames.previewWrapper}>
            <Group className={classNames.previewContainer}>
              {images.slice(0, showPreviews).map((image, index) =>
                image.path.includes(env.API_URL) ? (
                  <LoadedImage
                    key={index}
                    hideLoader
                    image={image}
                    //alt={image.name}
                    className={`${classNames.previewImage} ${index === activeIndex ? classNames.activePreview : ""}`}
                    onClick={() => onSlideChange(index)}
                    radius="md"
                  />
                ) : (
                  <Image
                    key={index}
                    src={image.path}
                    alt={image.name}
                    className={`${classNames.previewImage} ${index === activeIndex ? classNames.activePreview : ""}`}
                    onClick={() => onSlideChange(index)}
                    radius="md"
                  />
                )
              )}
              {hasMoreImages && (
                <Box className={classNames.moreImages} onClick={() => onSlideChange(maxPreviews)}>
                  <Text>+{moreImagesCount}</Text>
                </Box>
              )}
            </Group>
          </Box>
        )}
      </Box>
      <Modal
        opened={opened}
        onClose={close}
        title={
          <>
            <Text className={classNames.galleryCounter}>
              {activeIndex + 1} / {images.length}
            </Text>

            {uploadingButton && (
              <Box
                className={classNames.galleryUploadButton}
                onClick={() => {
                  close();
                  onEditorButtonClick?.();
                }}
              >
                {uploadingButton}
              </Box>
            )}
          </>
        }
        centered
        fullScreen
        size="xl"
        classNames={{
          content: classNames.galleryModalContent,
          header: classNames.galleryModalHeader,
          title: classNames.mantineModalTitle,
          body: classNames.galleryModalBody,
        }}
      >
        <Box className={classNames.galleryModal}>
          <Box className={classNames.galleryModalImage}>
            {images.length > 1 && (
              <ActionIcon
                variant="filled"
                size="lg"
                radius="xl"
                pos="absolute"
                left="1rem"
                top="50%"
                style={{ transform: "translateY(-50%)", zIndex: 2 }}
                onClick={() => onSlideChange(activeIndex - 1)}
                aria-label="Previous image"
              >
                <IconArrowLeft />
              </ActionIcon>
            )}

            <Box className={classNames.galleryModalImageContent}>
              {images[activeIndex]?.id &&
                removableImageIds.includes(images[activeIndex].id) &&
                onRemoveImage && (
                  <ActionIcon
                    className={classNames.galleryDeleteButton}
                    variant="white"
                    color="#cc1e2e"
                    radius="lg"
                    loading={removingImage}
                    onClick={handleRemoveImage}
                    aria-label={t("common.delete")}
                    title={t("common.delete")}
                  >
                    <IconTrash size={28} />
                  </ActionIcon>
                )}
              {images[activeIndex]?.path.includes(env.API_URL) ? (
                <LoadedImage image={images[activeIndex]} hideLoader />
              ) : (
                <Image
                  src={images[activeIndex]?.path}
                  alt={images[activeIndex]?.name}
                  fit="contain"
                  h="100%"
                  w="100%"
                />
              )}

              {!images[activeIndex]?.id && images[activeIndex] && (
                <Badge
                  pos="absolute"
                  bottom={8}
                  right={8}
                  variant="white"
                  c="dark"
                  style={{
                    opacity: "70%",
                    cursor: "pointer",
                  }}
                  onClick={() => {
                    const image = images[activeIndex];

                    if (!image) return;

                    setFeedbackImage({
                      ...image,
                      id: `gallery-image-${activeIndex}`,
                    });
                    toggleFeedback();
                  }}
                >
                  <Flex align="center" gap="sm">
                    <IconFlag size={14} />
                    {t("car.carburateResource")}
                  </Flex>
                </Badge>
              )}
            </Box>

            {images.length > 1 && (
              <ActionIcon
                variant="filled"
                size="lg"
                radius="xl"
                pos="absolute"
                right="1rem"
                top="50%"
                style={{ transform: "translateY(-50%)", zIndex: 2 }}
                onClick={() => onSlideChange(activeIndex + 1)}
                aria-label="Next image"
              >
                <IconArrowRight />
              </ActionIcon>
            )}
          </Box>

          <Carousel
            className={classNames.galleryThumbnailStrip}
            draggable={false}
            withControls={false}
            withIndicators={false}
            slideSize="110px"
            height={100}
            getEmblaApi={setThumbnailEmbla}
            slides={images.map((image, index) => (
              <Carousel.Slide key={image.id ?? index}>
                <Box
                  className={`${classNames.galleryThumbnail} ${index === activeIndex ? classNames.galleryThumbnailActive : ""}`}
                  onClick={() => onSlideChange(index)}
                >
                  {image.path.includes(env.API_URL) ? (
                    <LoadedImage image={image} hideLoader />
                  ) : (
                    <Image src={image.path} alt={image.name} fit="cover" h="100%" w="100%" />
                  )}
                </Box>
              </Carousel.Slide>
            ))}
          />
        </Box>
      </Modal>

      <UserFeedback
        title={t("feedback.wrongImageTitle")}
        description={t("feedback.wrongImageDescription")}
        opened={feedbackOpened}
        image={feedbackImage}
        onOpenedChange={toggleFeedback}
      />
    </>
  );
};
